import { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { getUnixTime } from "date-fns";
import dayjs from "dayjs";
import downloadBlob from "../../../../utils/download-blob";

// Hook dùng chung cho 3 tab log (Error/Click/Access).
// Gom toàn bộ state + thao tác lặp lại: tải dữ liệu, phân trang, lọc theo
// khoảng thời gian, chọn dòng, xoá đã chọn / xoá tất cả theo lọc, drawer chi
// tiết, xuất CSV toàn bộ kết quả lọc, tự động làm mới.
//
// Tham số:
//   name           - tên hiển thị trong toast (vd "ErrorLog")
//   initialFilter  - object filter mặc định của tab
//   load(params)   - gọi API list, trả { data: { data, total } }
//   remove(ids)    - gọi API xoá nhiều theo id
//   removeAll(f)   - gọi API xoá tất cả theo filter
//   loadStats(f)   - gọi API stat card theo filter, trả { data: { data } }, optional
//   exportAll(f)   - gọi API xuất CSV toàn bộ kết quả lọc (blob), optional
//   onMutated()    - callback sau khi xoá (để cha refresh badge tab), optional
export function useLogTable({
    name,
    initialFilter,
    load,
    remove,
    removeAll,
    loadStats,
    exportAll,
    onMutated,
}) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [selected, setSelected] = useState([]);
    const [detail, setDetail] = useState(null);
    // f        = bộ lọc admin ĐANG GÕ trên thanh công cụ (đổi theo từng phím)
    // appliedF = bộ lọc ĐÃ ÁP DỤNG, tức đúng cái tạo ra `rows` và `total`
    //
    // Phải tách hai cái này: nếu xoá hàng loạt đọc `f` mà số dòng trong hộp xác
    // nhận lại đến từ `total` (của appliedF), admin sẽ thấy "Xoá 500 dòng" rồi
    // hệ thống xoá theo một bộ lọc rộng hơn hẳn — mất dữ liệu không hoàn tác.
    const [f, setF] = useState(initialFilter);
    const [appliedF, setAppliedF] = useState(initialFilter);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    // Tự động làm mới (giây); 0 = tắt. Hữu ích khi ngồi theo dõi lỗi đang xảy ra.
    const [autoRefresh, setAutoRefresh] = useState(0);
    // Tăng mỗi lần bộ lọc được áp dụng → biểu đồ / bảng nhóm lỗi vẽ lại theo.
    const [version, setVersion] = useState(0);
    const bumpVersion = () => setVersion((v) => v + 1);

    // Stat card theo filter. Chỉ chạy khi filter đổi (lọc/xoá/mount), KHÔNG khi đổi trang.
    const refreshStats = async (overrideF) => {
        if (!loadStats) return;
        setStatsLoading(true);
        try {
            const { data } = await loadStats(overrideF || f);
            setStats(data.data || null);
        } catch {
            // im lặng — card hiện 0; bảng vẫn hoạt động độc lập
        } finally {
            setStatsLoading(false);
        }
    };

    // Hàm thường (không memo) để luôn đọc filter `f` mới nhất khi bấm Lọc.
    // Tải khi gõ filter chỉ xảy ra khi gọi search()/reload() — không tự chạy.
    const fetchData = async (p = page, l = limit, overrideF) => {
        const used = overrideF || f;
        setLoading(true);
        try {
            const { data } = await load({ ...used, page: p, limit: l });
            setRows(data.data || []);
            setTotal(data.total || 0);
            // Chỉ ghi nhận là "đã áp dụng" khi request thành công — để total và
            // appliedF luôn mô tả cùng một tập dữ liệu.
            setAppliedF(used);
        } catch {
            message.error(`Không tải được ${name}`);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(1, limit); refreshStats(); setPage(1); }, []);

    const setField = (k, v) => setF((s) => ({ ...s, [k]: v }));

    const setRange = (v) =>
        setF((s) => ({
            ...s,
            from: v?.[0] ? getUnixTime(v[0].toDate()) : "",
            to: v?.[1] ? getUnixTime(v[1].toDate()) : "",
        }));

    // Giá trị hiển thị cho RangePicker — suy ra từ f.from/f.to nên khoảng thời
    // gian chọn bằng nút lọc nhanh cũng hiện lại đúng trong ô ngày.
    const rangeValue = useMemo(
        () => (f.from && f.to ? [dayjs.unix(f.from), dayjs.unix(f.to)] : null),
        [f.from, f.to]
    );

    // Chọn nhanh khoảng thời gian (unix giây) rồi lọc luôn.
    // Truyền filter mới trực tiếp vào fetchData để tránh stale state.
    const applyQuickRange = (from, to) => {
        const nf = { ...f, from, to };
        setF(nf);
        setPage(1);
        fetchData(1, limit, nf);
        refreshStats(nf);
        bumpVersion();
    };

    // Áp một phần bộ lọc rồi lọc luôn (vd bấm 1 nhóm lỗi → lọc theo path đó).
    const applyFilter = (patch) => {
        const nf = { ...f, ...patch };
        setF(nf);
        setPage(1);
        fetchData(1, limit, nf);
        refreshStats(nf);
        bumpVersion();
    };

    const search = () => { setPage(1); fetchData(1, limit); refreshStats(); bumpVersion(); };
    // reload() cũng là hàm mà bộ đếm tự-làm-mới gọi → phải dùng appliedF, nếu
    // không mỗi tick sẽ âm thầm áp dụng bộ lọc admin mới gõ dở.
    const reload = () => {
        fetchData(page, limit, appliedF);
        refreshStats(appliedF);
        bumpVersion();
    };

    // Đưa mọi ô lọc về mặc định rồi tải lại — trước đây phải xoá tay từng ô.
    const clear = () => {
        setF(initialFilter);
        setSelected([]);
        setPage(1);
        fetchData(1, limit, initialFilter);
        refreshStats(initialFilter);
        bumpVersion();
    };

    // Nút "Xoá lọc" bám theo cái admin đang gõ.
    const hasFilter = useMemo(
        () => Object.values(f).some((v) => v !== "" && v != null),
        [f]
    );

    // Chốt chặn xoá hàng loạt bám theo cái ĐÃ áp dụng — cùng nguồn với `total`.
    const appliedHasFilter = useMemo(
        () => Object.values(appliedF).some((v) => v !== "" && v != null),
        [appliedF]
    );

    // Đang gõ dở, chưa bấm Lọc → khoá nút xoá hàng loạt cho tới khi áp dụng,
    // tránh cảnh nút hiện số của bộ lọc cũ trong khi admin nghĩ tới bộ lọc mới.
    const isDirty = useMemo(
        () => JSON.stringify(f) !== JSON.stringify(appliedF),
        [f, appliedF]
    );

    const onPageChange = (p, l) => {
        const nl = l || limit;
        setPage(p);
        setLimit(nl);
        fetchData(p, nl);
    };

    const del = async () => {
        try {
            await remove(selected);
            message.success(`Đã xoá ${selected.length} dòng`);
            setSelected([]);
            fetchData(page, limit);
            refreshStats();
            bumpVersion();
            onMutated?.();
        } catch {
            message.error("Xoá thất bại");
        }
    };

    const delAll = async () => {
        try {
            // appliedF, KHÔNG phải f — phải xoá đúng tập mà `total` đang đếm.
            const { data } = await removeAll(appliedF);
            message.success(`Đã xoá ${data?.deleted || 0} dòng theo bộ lọc`);
            setSelected([]);
            setPage(1);
            fetchData(1, limit);
            refreshStats();
            bumpVersion();
            onMutated?.();
        } catch (e) {
            message.error(e?.response?.data?.message || "Xoá thất bại");
        }
    };

    // Tải CSV toàn bộ kết quả lọc từ server (blob) — không chỉ trang đang xem.
    const downloadCsv = async () => {
        if (!exportAll) return;
        setExporting(true);
        try {
            // appliedF để file tải về khớp đúng với bảng đang hiển thị.
            const res = await exportAll(appliedF);
            downloadBlob(res.data, `${name.toLowerCase()}-${Date.now()}.csv`);
        } catch {
            message.error("Xuất CSV thất bại (thử thu hẹp khoảng thời gian)");
        } finally {
            setExporting(false);
        }
    };

    // Tự động làm mới. Dùng ref cho hàm tải để interval luôn gọi bản mới nhất
    // mà không phải dựng lại timer mỗi lần state đổi.
    const reloadRef = useRef(reload);
    reloadRef.current = reload;
    useEffect(() => {
        if (!autoRefresh) return undefined;
        const id = setInterval(() => reloadRef.current(), autoRefresh * 1000);
        return () => clearInterval(id);
    }, [autoRefresh]);

    return {
        rows, loading, total, page, limit, selected, setSelected,
        detail, openDetail: setDetail, closeDetail: () => setDetail(null),
        f, appliedF, setField, setRange, rangeValue, applyQuickRange, applyFilter, clear,
        hasFilter, appliedHasFilter, isDirty,
        stats, statsLoading, version,
        exporting, downloadCsv,
        autoRefresh, setAutoRefresh,
        search, reload, onPageChange, del, delAll,
    };
}
